// ELS MISSATGES D'ERROR SÓN CLAUS, no text.
//
// Un thunk no pot cridar `t()` —no és dins de cap component— i per això aquí hi havia text
// anglès escrit al codi, que **es veia a la capçalera de l'editor** encara que l'aplicació
// anés en català. Ara el que viatja és la clau i qui la pinta la tradueix.
//
// **El que això NO arregla**: quan l'API sí que envia un missatge, aquell missatge és seu i
// també és en anglès. Arreglar-ho de debò vol dir que el servidor enviï codis, com ja fa
// amb `reason: 'pending'` de l'autenticació. Queda pendent.
import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import modelService from '../services/modelService';

const initialState = {
  models: [],
  currentModel: null,
  loading: false,
  error: null,
  totalPages: 1,
  currentPage: 1,
  // Estat del desat del dibuix, separat de `loading` a posta: `loading` el fan
  // servir les pantalles per tapar-se senceres amb un indicador, i desar no ha
  // de fer desaparèixer el canvas que s'està editant.
  savingComponents: false,
  componentsError: null,
};

export const fetchModels = createAsyncThunk(
  'models/fetchAll',
  async ({ page = 1, type, arxivats } = {}, { rejectWithValue }) => {
    try {
      const response = await modelService.getModels({ page, type, arxivats });
      return response;
    } catch (error) {
      return rejectWithValue(error.response?.data?.error || 'errors.fetchModels');
    }
  }
);

export const fetchModel = createAsyncThunk(
  'models/fetchOne',
  async (id, { rejectWithValue }) => {
    try {
      const response = await modelService.getModel(id);
      return response;
    } catch (error) {
      return rejectWithValue(error.response?.data?.error || 'errors.fetchModel');
    }
  }
);

export const createModel = createAsyncThunk(
  'models/create',
  async (modelData, { rejectWithValue }) => {
    try {
      const response = await modelService.createModel(modelData);
      return response;
    } catch (error) {
      return rejectWithValue(error.response?.data?.error || 'errors.createModel');
    }
  }
);

export const updateModel = createAsyncThunk(
  'models/update',
  async ({ id, data }, { rejectWithValue }) => {
    try {
      const response = await modelService.updateModel(id, data);
      return response;
    } catch (error) {
      return rejectWithValue(error.response?.data?.error || 'errors.updateModel');
    }
  }
);

export const deleteModel = createAsyncThunk(
  'models/delete',
  async (id, { rejectWithValue }) => {
    try {
      await modelService.deleteModel(id);
      return id;
    } catch (error) {
      return rejectWithValue(error.response?.data?.error || 'errors.deleteModel');
    }
  }
);

export const arxivaModel = createAsyncThunk(
  'models/arxiva',
  async ({ id, arxivat }, { rejectWithValue }) => {
    try {
      await modelService.arxiva(id, arxivat);
      return id;
    } catch (error) {
      return rejectWithValue(error.response?.data?.error || 'errors.arxivaModel');
    }
  }
);

export const assignaModel = createAsyncThunk(
  'models/assigna',
  async ({ id, usuaris }, { rejectWithValue }) => {
    try {
      const r = await modelService.assigna(id, usuaris);
      return { id, sharedWith: r.sharedWith };
    } catch (error) {
      return rejectWithValue(error.response?.data?.error || 'errors.assignaModel');
    }
  }
);

export const fetchModelComponents = createAsyncThunk(
  'models/fetchComponents',
  async (modelId, { rejectWithValue }) => {
    try {
      return await modelService.getComponents(modelId);
    } catch (error) {
      return rejectWithValue(error.response?.data?.error || 'errors.fetchComponents');
    }
  }
);

export const saveModelComponents = createAsyncThunk(
  'models/saveComponents',
  async ({ modelId, components, basatEn }, { rejectWithValue }) => {
    try {
      return await modelService.saveComponents(modelId, components, basatEn);
    } catch (error) {
      return rejectWithValue(error.response?.data?.error || 'errors.saveComponents');
    }
  }
);

export const fetchSoftSystems = createAsyncThunk(
  'models/fetchSoftSystems',
  async (modelId, { rejectWithValue }) => {
    try {
      return await modelService.getSoftSystems(modelId);
    } catch (error) {
      return rejectWithValue(error.response?.data?.error || 'errors.fetchSSM');
    }
  }
);

export const saveSoftSystems = createAsyncThunk(
  'models/saveSoftSystems',
  async ({ modelId, dades, basatEn }, { rejectWithValue }) => {
    try {
      return await modelService.saveSoftSystems(modelId, dades, basatEn);
    } catch (error) {
      return rejectWithValue(error.response?.data?.error || 'errors.saveSSM');
    }
  }
);

export const fetchCausalLoop = createAsyncThunk(
  'models/fetchCausalLoop',
  async (modelId, { rejectWithValue }) => {
    try {
      return await modelService.getCausalLoop(modelId);
    } catch (error) {
      return rejectWithValue(error.response?.data?.error || 'errors.fetchCausal');
    }
  }
);

export const saveCausalLoop = createAsyncThunk(
  'models/saveCausalLoop',
  async ({ modelId, variables, relationships, basatEn }, { rejectWithValue }) => {
    try {
      return await modelService.saveCausalLoop(modelId, variables, relationships, basatEn);
    } catch (error) {
      return rejectWithValue(error.response?.data?.error || 'errors.saveCausal');
    }
  }
);

const modelSlice = createSlice({
  name: 'models',
  initialState,
  reducers: {
    clearCurrentModel: (state) => {
      state.currentModel = null;
    },
    updateCurrentModelLocally: (state, action) => {
      if (state.currentModel) {
        state.currentModel = { ...state.currentModel, ...action.payload };
      }
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchModels.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchModels.fulfilled, (state, action) => {
        state.loading = false;
        state.models = action.payload.models;
        state.totalPages = action.payload.totalPages;
        state.currentPage = action.payload.page;
      })
      .addCase(fetchModels.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      .addCase(fetchModel.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchModel.fulfilled, (state, action) => {
        state.loading = false;
        state.currentModel = action.payload;
      })
      .addCase(fetchModel.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      .addCase(createModel.fulfilled, (state, action) => {
        state.models.unshift(action.payload);
      })
      .addCase(updateModel.fulfilled, (state, action) => {
        const index = state.models.findIndex(m => m.id === action.payload.id);
        if (index !== -1) {
          state.models[index] = action.payload;
        }
        if (state.currentModel?.id === action.payload.id) {
          state.currentModel = action.payload;
        }
      })
      // Arxivar treu el model de la llista que es mira ara: si mires els actius, l'arxivat en
      // surt, i si mires els arxivats, el que es desarxiva també. Les dues llistes són
      // excloents, o sigui que el comportament és el mateix i n'hi ha prou amb un cas.
      .addCase(arxivaModel.fulfilled, (state, action) => {
        state.models = state.models.filter((m) => m.id !== action.payload);
      })
      .addCase(assignaModel.fulfilled, (state, action) => {
        const m = state.models.find((x) => x.id === action.payload.id);
        if (m) m.sharedWith = action.payload.sharedWith;
      })
      .addCase(deleteModel.fulfilled, (state, action) => {
        state.models = state.models.filter(m => m.id !== action.payload);
        if (state.currentModel?.id === action.payload) {
          state.currentModel = null;
        }
      })
      .addCase(fetchModelComponents.fulfilled, (state, action) => {
        if (state.currentModel) {
          state.currentModel.components = action.payload;
        }
      })
      .addCase(fetchModelComponents.rejected, (state, action) => {
        state.componentsError = action.payload;
      })
      .addCase(saveModelComponents.pending, (state) => {
        state.savingComponents = true;
        state.componentsError = null;
      })
      .addCase(saveModelComponents.fulfilled, (state, action) => {
        state.savingComponents = false;
        if (state.currentModel) {
          // Els components tornen amb els identificadors de la base, que
          // substitueixen els provisionals del canvas.
          state.currentModel.components = action.payload.components;
          // I la data nova, o el segon desat xocaria amb el primer.
          if (action.payload.versio) state.currentModel.updatedAt = action.payload.versio;
        }
      })
      .addCase(saveModelComponents.rejected, (state, action) => {
        state.savingComponents = false;
        state.componentsError = action.payload;
      })
      // Els bucles causals comparteixen els indicadors de desat amb els components: per a qui
      // mira la pantalla és el mateix botó i el mateix «Desat», i el tipus de model decideix
      // quina de les dues coses es desa.
      // El Soft Systems comparteix els indicadors de desat amb la resta: per a qui mira la
      // pantalla és el mateix botó i el mateix «Desat».
      .addCase(fetchSoftSystems.fulfilled, (state, action) => {
        if (state.currentModel) state.currentModel.softSystemsModel = action.payload;
      })
      .addCase(fetchSoftSystems.rejected, (state, action) => {
        state.componentsError = action.payload;
      })
      .addCase(saveSoftSystems.pending, (state) => {
        state.savingComponents = true;
        state.componentsError = null;
      })
      .addCase(saveSoftSystems.fulfilled, (state, action) => {
        state.savingComponents = false;
        if (state.currentModel) {
          // El servidor torna les set etapes; el que no s'ha enviat torna tal com era a la base.
          state.currentModel.softSystemsModel = action.payload;
          if (action.payload.versio) state.currentModel.updatedAt = action.payload.versio;
        }
      })
      .addCase(saveSoftSystems.rejected, (state, action) => {
        state.savingComponents = false;
        state.componentsError = action.payload;
      })
      .addCase(fetchCausalLoop.fulfilled, (state, action) => {
        if (state.currentModel) {
          state.currentModel.causalLoopModel = {
            ...(state.currentModel.causalLoopModel || {}),
            variables: action.payload.variables,
            relationships: action.payload.relationships,
          };
        }
      })
      .addCase(fetchCausalLoop.rejected, (state, action) => {
        state.componentsError = action.payload;
      })
      .addCase(saveCausalLoop.pending, (state) => {
        state.savingComponents = true;
        state.componentsError = null;
      })
      .addCase(saveCausalLoop.fulfilled, (state, action) => {
        state.savingComponents = false;
        if (state.currentModel) {
          // Tornen amb els UUID de la base, que substitueixen els provisionals del llenç. Les
          // relacions ja hi apunten: el servidor les ha reescrit amb la correspondència.
          state.currentModel.causalLoopModel = {
            ...(state.currentModel.causalLoopModel || {}),
            variables: action.payload.variables,
            relationships: action.payload.relationships,
          };
          if (action.payload.versio) state.currentModel.updatedAt = action.payload.versio;
        }
      })
      .addCase(saveCausalLoop.rejected, (state, action) => {
        state.savingComponents = false;
        state.componentsError = action.payload;
      });
  },
});

export const { clearCurrentModel, updateCurrentModelLocally } = modelSlice.actions;
export default modelSlice.reducer;